import {usePDPSearchParams} from './use-pdp-search-params'
import {useCallback, useMemo} from 'react'
import {useHistory} from 'react-router-dom'
import {updateSearchParams} from '../utils/url'

/**
 * Hook to manage the selected state of a product in a set
 *
 * **Warning:** This hook is tightly coupled with useStoredConfiguration hook,
 * so any changes should be tested in conjunction with useStoredConfiguration
 *
 * @param {object} product - The product object containing setProductId
 * @return {[boolean, function, boolean, URLSearchParams]} Returns a tuple containing:
 *   - isSelected: whether the product is currently selected (based on URL params)
 *   - setSelected: function to update selection state, accepts (newState, extraProps)
 *     - newState (boolean): true to select, false to deselect
 *     - extraProps (object, optional): additional URL params to set/update (e.g., {pid: '123', color: 'red'})
 *       Pass null values to remove params (e.g., {color: null})
 *   - isSelectionChanging: whether URL state differs from product.isSetProductSelected
 *   - productParams: URLSearchParams for this specific product
 *
 * @example
 * const [isSelected, setSelected] = useSelectableSetProduct(product)
 *
 * // Select product
 * setSelected(true)
 *
 * // Select with additional params
 * setSelected(true, {pid: '123', color: 'red'})
 *
 * // Deselect while preserving other params
 * setSelected(false)
 *
 * // Deselect and remove params
 * setSelected(false, {pid: null, color: null})
 */
const useSelectableSetProduct = (product) => {
    const history = useHistory()
    const [, productParams] = usePDPSearchParams(product?.setProductId)
    // directly compute the selected state from the search params as they update much quicker than the product which needs to be fetched
    const isSelected = useMemo(() => productParams.get('selected') === '1', [productParams])
    const isSelectionChanging = useMemo(
        () => isSelected !== product?.isSetProductSelected ?? false,
        [isSelected, product]
    )

    const setSelected = useCallback(
        (newState, extraProps) => {
            // without product, do nothing
            if (!product?.setProductId) {
                return
            }

            // use history.location as it is always up to date
            const {pathname, search, state} = history.location
            const allParams = new URLSearchParams(search)
            const productParams = new URLSearchParams(allParams.get(product.setProductId) || '')

            // update the set product state
            updateSearchParams(productParams, {
                ...extraProps,
                selected: newState ? '1' : null
            })

            // update all params with the set product params
            updateSearchParams(allParams, {
                [product.setProductId]: productParams.toString()
            })

            const newSearch = allParams.toString()
            if (newSearch !== search.substring(1)) {
                history.replace(`${pathname}${newSearch ? `?${newSearch}` : ''}`, state)
            }
        },
        [history, product?.setProductId]
    )

    return [isSelected, setSelected, isSelectionChanging, productParams]
}

export default useSelectableSetProduct
